/**
 * Share Helper - Opens native share sheet (WhatsApp, etc.) on mobile
 * With safe fallback for copy when share sheet not available
 * 
 * CRITICAL: Share URLs use clean frontend routes (/p/{slug}) which:
 * 1. For crawlers: OG middleware serves proper meta tags
 * 2. For browsers: React SPA renders the profile page
 * 
 * IMPORTANT: Share links MUST use custom domain (talentmeet.in), never Emergent preview URLs
 */

import { toast } from "sonner";

// Cache for the base URL - ALWAYS prefer custom domain
let cachedBaseUrl = null;
let cacheExpiry = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Default custom domain - NEVER use preview URLs for sharing
const DEFAULT_CUSTOM_DOMAIN = 'https://www.talentmeet.in';

/**
 * Fetch the public base URL from platform settings
 * ALWAYS uses custom_domain from platform settings, NEVER falls back to preview URLs
 */
async function fetchPublicBaseUrl() {
  const now = Date.now();
  
  // Return cached value if still valid
  if (cachedBaseUrl && now < cacheExpiry) {
    return cachedBaseUrl;
  }
  
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${backendUrl}/api/public/platform-settings`);
    
    if (response.ok) {
      const settings = await response.json();
      if (settings.custom_domain) {
        let domain = settings.custom_domain.trim();
        // Ensure https:// prefix
        if (!domain.startsWith('http')) {
          domain = `https://${domain}`;
        }
        // Remove trailing slash
        domain = domain.replace(/\/$/, '');
        cachedBaseUrl = domain;
        cacheExpiry = now + CACHE_DURATION;
        return cachedBaseUrl;
      }
    }
  } catch (err) {
    console.warn("[Share] Failed to fetch platform settings:", err.message);
  }
  
  // CRITICAL: Always use default custom domain, NEVER preview URL
  // Preview URLs should never be shared externally
  cachedBaseUrl = DEFAULT_CUSTOM_DOMAIN;
  cacheExpiry = now + CACHE_DURATION;
  return cachedBaseUrl;
}

/**
 * Get the public base URL synchronously (uses cache or fallback)
 * For sync operations, prefer getPublicBaseUrlAsync when possible
 * CRITICAL: Always returns custom domain, never preview URL
 */
function getPublicBaseUrlSync() {
  // If we have a cached value, use it
  if (cachedBaseUrl && Date.now() < cacheExpiry) {
    return cachedBaseUrl;
  }
  
  // CRITICAL: Always use custom domain, NEVER preview URL
  return DEFAULT_CUSTOM_DOMAIN;
}

/**
 * DOM fallback copy - works even when Clipboard API is blocked
 */
function domFallbackCopy(text) {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch (err) {
    console.warn("[Share] DOM copy failed:", err);
    return false;
  }
}

/**
 * Safe copy to clipboard - NEVER throws
 */
export async function safeCopyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn("[Share] Clipboard API blocked:", err.message);
    }
  }
  return domFallbackCopy(text);
}

/**
 * Get canonical share URL for profile (async version - preferred)
 * 
 * Uses CLEAN frontend URLs (/p/{slug}) - NOT API endpoints
 * OG middleware handles crawler requests and serves proper meta tags
 * 
 * URL structure: {baseUrl}/p/{slug}
 */
export async function getProfileShareUrlAsync(profile, role) {
  const baseUrl = await fetchPublicBaseUrl();
  const slug = profile?.slug;
  const profileId = profile?.profile_id || profile?._id;
  
  // Use clean frontend URL - OG middleware handles crawlers
  if (slug) {
    return `${baseUrl}/p/${slug}`;
  }
  
  // Fallback to profile ID if no slug
  if (profileId) {
    return `${baseUrl}/profile/${profileId}`;
  }
  
  return `${baseUrl}/browse`;
}

/**
 * Get canonical share URL for profile (sync version - for backward compatibility)
 * Uses CLEAN frontend URLs (/p/{slug}) - NOT API endpoints
 */
export function getProfileShareUrl(profile, role) {
  const baseUrl = getPublicBaseUrlSync();
  const slug = profile?.slug;
  const profileId = profile?.profile_id || profile?._id;
  
  // Use clean frontend URL - OG middleware handles crawlers
  if (slug) {
    return `${baseUrl}/p/${slug}`;
  }
  
  if (profileId) {
    return `${baseUrl}/profile/${profileId}`;
  }
  
  return `${baseUrl}/browse`;
}

/**
 * Get project share URL (async version - preferred)
 * Uses CLEAN frontend URL /share/project/{token}
 * OG middleware handles crawler requests and serves proper meta tags
 * 
 * URL structure: {baseUrl}/share/project/{token}
 */
export async function getProjectShareUrlAsync(token) {
  const baseUrl = await fetchPublicBaseUrl();
  if (!token) {
    return `${baseUrl}/browse`;
  }
  // Use clean frontend URL - OG middleware handles crawlers
  return `${baseUrl}/share/project/${token}`;
}

/**
 * Get project share URL (sync version)
 * Uses CLEAN frontend URL /share/project/{token}
 */
export function getProjectShareUrl(token) {
  const baseUrl = getPublicBaseUrlSync();
  if (!token) {
    return `${baseUrl}/browse`;
  }
  // Use clean frontend URL - OG middleware handles crawlers
  return `${baseUrl}/share/project/${token}`;
}

/**
 * Get direct profile URL (for internal navigation)
 */
export function getProfileDirectUrl(profile, role) {
  const slug = profile?.slug;
  const profileId = profile?.profile_id || profile?._id;
  
  if (slug) {
    return `/p/${slug}`;
  }
  
  if (profileId) {
    return `/profile/${profileId}`;
  }
  
  return `/browse`;
}

/**
 * Get public base URL (async - fetches from platform settings)
 */
export async function getPublicBaseUrlAsync() {
  return fetchPublicBaseUrl();
}

/**
 * Get public base URL (sync - uses cache or window.location.origin)
 */
export function getPublicBaseUrl() {
  return getPublicBaseUrlSync();
}

/**
 * Build a public URL path (async version)
 */
export async function getPublicUrlAsync(path) {
  const baseUrl = await fetchPublicBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Build a public URL path (sync version)
 */
export function getPublicUrl(path) {
  const baseUrl = getPublicBaseUrlSync();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Initialize the cache (call this early in app lifecycle)
 */
export async function initializeShareHelper() {
  await fetchPublicBaseUrl();
}

/**
 * MAIN SHARE FUNCTION
 * 
 * 1. Try native share sheet (WhatsApp, Messages, etc.)
 * 2. Fallback to clipboard copy
 * 3. Fallback to WhatsApp web link
 */
export async function handleShare({ url, title, text, onShowModal }) {
  const shareData = {
    title: title || "TalentMeet",
    text: text || "Check this out on TalentMeet",
    url: url
  };

  // STEP 1: Try native share sheet
  if (navigator.share) {
    try {
      if (navigator.canShare && !navigator.canShare(shareData)) {
        await navigator.share({ url });
      } else {
        await navigator.share(shareData);
      }
      toast.success("Shared!");
      return { success: true, method: "native" };
    } catch (err) {
      if (err.name === "AbortError") {
        return { success: false, method: "cancelled" };
      }
      console.warn("[Share] Native share failed:", err.message);
    }
  }

  // STEP 2: Try clipboard copy
  const copySuccess = await safeCopyToClipboard(url);
  if (copySuccess) {
    toast.success("Link copied to clipboard!");
    return { success: true, method: "clipboard" };
  }

  // STEP 3: Show modal if provided
  if (onShowModal) {
    onShowModal(url);
    return { success: false, method: "modal" };
  }

  // STEP 4: WhatsApp fallback
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${text || "Check this out"}: ${url}`)}`;
  window.open(whatsappUrl, "_blank");
  toast.info("Opening WhatsApp to share...");
  return { success: true, method: "whatsapp" };
}

/**
 * Quick share for profile (async version - preferred)
 */
export async function quickShareAsync(profile, role, onShowModal) {
  const url = await getProfileShareUrlAsync(profile, role);
  const name = profile?.personal_info?.display_name || profile?.personal_info?.full_name || "Profile";
  
  return handleShare({
    url,
    title: `${name} - TalentMeet`,
    text: `View ${name}'s profile on TalentMeet`,
    onShowModal
  });
}

/**
 * Quick share for profile (sync version - backward compatibility)
 */
export async function quickShare(profile, role, onShowModal) {
  // Use async version internally for better URL resolution
  return quickShareAsync(profile, role, onShowModal);
}

// Exports
export const shareProfile = handleShare;
export const copyToClipboard = safeCopyToClipboard;

export default {
  handleShare,
  quickShare,
  quickShareAsync,
  getProfileShareUrl,
  getProfileShareUrlAsync,
  getProjectShareUrl,
  getProjectShareUrlAsync,
  getProfileDirectUrl,
  getPublicBaseUrl,
  getPublicBaseUrlAsync,
  getPublicUrl,
  getPublicUrlAsync,
  safeCopyToClipboard,
  initializeShareHelper,
  shareProfile: handleShare,
  copyToClipboard: safeCopyToClipboard
};
